import React, { useEffect, useMemo, useState, useRef } from "react";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useDispatch, useSelector } from "react-redux";
import {
  Download as DownloadIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import XLSX from "xlsx-js-style";
import { saveAs } from "file-saver";
import { motion } from "framer-motion";
import AttendanceMonthlyToggle from "./AttendanceMonthlyToggle";

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Stack,
  DialogActions,
  TextField,
  InputAdornment,
  CircularProgress,
  Chip,
  useTheme,
  Button,
  IconButton,
  TablePagination,
  Grid,
  DialogContent,
  Dialog,
  DialogTitle,
  Autocomplete,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";

import {
  Search as SearchIcon,
  AccessTime as AccessTimeIcon,
  Logout as LogoutIcon,
  Phone as PhoneIcon,
  Badge as BadgeIcon,
  LocationOn as LocationOnIcon,
  EventAvailable as EventAvailableIcon,
  Pending as PendingIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Clear as ClearIcon,
  Close as CloseIcon,
  CalendarToday as CalendarTodayIcon,
} from "@mui/icons-material";

import attendanceSvg from "assets/folders/attendance.svg";

import {
  fetchAttendanceRecords,
  verifyAttendance,
  fetchMonthlyAttendanceReport,
  fetchLeaveCodes,
  fetchEmployeeLeaveReport,
} from "store/features/company/attendanceManagement/attendanceRecords/attendanceRecordsActions";
import { fetchEmployees } from "store/features/company/employee/employeeActions";
import { selectEmployees } from "store/features/company/employee/employeeSelectors";
import { selectCurrentUser } from "store/features/auth/authSelectors";

import {
  selectAttendanceRecords,
  selectAttendanceRecordsLoading,
  selectAttendanceRecordsPagination,
} from "store/features/company/attendanceManagement/attendanceRecords/attendanceRecordsSelectors";

import usePermissions from "hooks/usePermissions";

const AttendanceRecords = () => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const user = useSelector(selectCurrentUser);
  const userRole = user?.role;
  const attendanceRecords = useSelector(selectAttendanceRecords);
  const loading = useSelector(selectAttendanceRecordsLoading);
  const pagination = useSelector(selectAttendanceRecordsPagination);
  const employees = useSelector(selectEmployees);

  // Filter states
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [checkInModalOpen, setCheckInModalOpen] = useState(false);
  const [checkOutModalOpen, setCheckOutModalOpen] = useState(false);
  const [selectedPicture, setSelectedPicture] = useState("");
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [viewMode, setViewMode] = useState("daily");
  const [selectedMonth, setSelectedMonth] = useState(dayjs());
  const [legendModalOpen, setLegendModalOpen] = useState(false);
  const [leaveCodes, setLeaveCodes] = useState({});
  const [loadingLegend, setLoadingLegend] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const employeeRefs = useRef({});

  const { hasPermission } = usePermissions();
  const canApprove =
    hasPermission("attendance.approve") ||
    hasPermission("attandance.approve") ||
    userRole === "Company";

  const handleVerify = async (id, approval_status) => {
    setActionLoadingId(id);
    const currentParams = {};
    if (statusFilter !== "all") currentParams.status = statusFilter;
    if (fromDate) currentParams.from_date = fromDate;
    if (toDate) currentParams.to_date = toDate;
    if (selectedEmployee) currentParams.employee_id = selectedEmployee.id;
    if (searchTerm) currentParams.search = searchTerm;
    currentParams.page = page + 1;
    currentParams.limit = rowsPerPage;

    await dispatch(verifyAttendance(id, { approval_status }, currentParams));
    setActionLoadingId(null);
  };

  useEffect(() => {
    dispatch(fetchEmployees());
  }, [dispatch]);

  const memoizedEmployees = useMemo(() => {
    return attendanceRecords.map((employee) => {
      const year = pagination?.year || selectedMonth.year();
      const month = pagination?.month || selectedMonth.month() + 1;
      const monthStr = String(month).padStart(2, "0");

      const attendance = employee.attendance || {};
      const dayNumbers = Object.keys(attendance)
        .map(Number)
        .sort((a, b) => a - b);
      const daysInMonth =
        dayNumbers.length > 0
          ? dayNumbers
          : Array.from({ length: 31 }, (_, i) => i + 1);
      const isConfirmed = employee.isConfirmed || false;

      return {
        ...employee,
        year,
        month,
        monthStr,
        daysInMonth,
        isConfirmed,
      };
    });
  }, [attendanceRecords, pagination, selectedMonth]);

  //for monthly view search

  useEffect(() => {
    if (viewMode === "monthly" && searchTerm) {
      const matchedEmployee = memoizedEmployees.find((emp) => {
        const search = searchTerm.toLowerCase();
        const name = (emp.employeeName || "").toLowerCase();
        const code = (emp.employeeCode || "").toLowerCase();
        return name.includes(search) || code.includes(search);
      });

      if (matchedEmployee && employeeRefs.current[matchedEmployee.employeeId]) {
        employeeRefs.current[matchedEmployee.employeeId].scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }
  }, [searchTerm, viewMode, memoizedEmployees]);
  useEffect(() => {
    if (viewMode === "monthly") {
      const year = selectedMonth.year();
      const month = selectedMonth.month() + 1;

      dispatch(fetchMonthlyAttendanceReport({ year, month }));
    } else {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      if (selectedEmployee) params.employee_id = selectedEmployee.id;
      if (searchTerm) params.search = searchTerm;
      params.page = page + 1;
      params.limit = rowsPerPage;
      dispatch(fetchAttendanceRecords(params));
    }
  }, [
    viewMode,
    selectedMonth,
    statusFilter,
    fromDate,
    toDate,
    selectedEmployee,
    searchTerm,
    page,
    rowsPerPage,
    dispatch,
  ]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedEmployee(null);
    setStatusFilter("all");
    setFromDate("");
    setToDate("");
    setPage(0);
  };

  const getLeaveCodesData = async () => {
    setLoadingLegend(true);
    try {
      const result = await dispatch(fetchLeaveCodes());
      if (result.success) {
        setLeaveCodes(result.data);
      }
    } catch (error) {
      console.error("Error fetching leave codes:", error);
    } finally {
      setLoadingLegend(false);
    }
  };

  const handleViewLegend = () => {
    if (Object.keys(leaveCodes).length === 0) {
      getLeaveCodesData();
    }
    setLegendModalOpen(true);
  };

  const filteredRecords = useMemo(() => {
    let filtered = attendanceRecords || [];

    if (searchTerm && !selectedEmployee) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter((record) => {
        const fullName = `${record.first_name || ""} ${
          record.last_name || ""
        }`.toLowerCase();
        const phone = record.phone || "";
        const code = record.employee_code || "";
        const status = record.status || "";

        return (
          fullName.includes(search) ||
          phone.includes(search) ||
          code.toLowerCase().includes(search) ||
          status.includes(search)
        );
      });
    }

    return filtered;
  }, [attendanceRecords, searchTerm, selectedEmployee]);

  const getOrdinalSuffix = (day) => {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const formatDateHeader = (dateStr) => {
    if (!dateStr || dateStr === "Unknown") return "Date Unknown";
    const cleanDate = dateStr.includes("T") ? dateStr.split("T")[0] : dateStr;
    const parts = cleanDate.split("-");
    if (parts.length < 3) return dateStr;
    const date = new Date(
      parseInt(parts[0]),
      parseInt(parts[1]) - 1,
      parseInt(parts[2]),
    );
    if (isNaN(date.getTime())) return dateStr;

    const weekday = date.toLocaleDateString("en-US", { weekday: "long" });
    const dayNum = date.getDate();
    const month = date.toLocaleDateString("en-US", { month: "long" });
    const year = date.getFullYear();
    const suffix = getOrdinalSuffix(dayNum);

    return `${weekday}, ${dayNum}${suffix} ${month} ${year}`;
  };

  const groupedRecordsByDate = useMemo(() => {
    const groups = {};
    (filteredRecords || []).forEach((record) => {
      let dateKey = record.record_date;
      if (!dateKey && record.check_in) {
        dateKey = record.check_in.split("T")[0];
      }
      if (!dateKey && record.created_at) {
        dateKey = record.created_at.split("T")[0];
      }
      if (!dateKey) {
        dateKey = "Unknown";
      }
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(record);
    });
    return groups;
  }, [filteredRecords]);

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatMinutes = (minutes) => {
    if (!minutes || minutes === 0) return "0 min";
    const hrs = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hrs === 0) return `${mins} min`;
    return `${hrs}h ${mins}m`;
  };

  const getStatusColor = (status) => {
    const colors = {
      present: {
        bg: "#d1fae5",
        text: "#065f46",
        darkBg: "rgba(16,185,129,0.25)",
        darkText: "#6ee7b7",
      },
      holiday_present: {
        bg: "#d1fae5",
        text: "#065f46",
        darkBg: "rgba(16,185,129,0.25)",
        darkText: "#6ee7b7",
      },
      hp: {
        bg: "#d1fae5",
        text: "#065f46",
        darkBg: "rgba(16,185,129,0.25)",
        darkText: "#6ee7b7",
      },
      week_off_present: {
        bg: "#d1fae5",
        text: "#065f46",
        darkBg: "rgba(16,185,129,0.25)",
        darkText: "#6ee7b7",
      },
      wop: {
        bg: "#d1fae5",
        text: "#065f46",
        darkBg: "rgba(16,185,129,0.25)",
        darkText: "#6ee7b7",
      },
      leave_present: {
        bg: "#d1fae5",
        text: "#065f46",
        darkBg: "rgba(16,185,129,0.25)",
        darkText: "#6ee7b7",
      },
      lp: {
        bg: "#d1fae5",
        text: "#065f46",
        darkBg: "rgba(16,185,129,0.25)",
        darkText: "#6ee7b7",
      },
      absent: {
        bg: "#fee2e2",
        text: "#991b1b",
        darkBg: "rgba(239,68,68,0.25)",
        darkText: "#fca5a5",
      },
      late: {
        bg: "#fef3c7",
        text: "#92400e",
        darkBg: "rgba(245,158,11,0.25)",
        darkText: "#fde68a",
      },
      holiday: {
        bg: "#dbeafe",
        text: "#1e40af",
        darkBg: "rgba(59,130,246,0.25)",
        darkText: "#93c5fd",
      },
      week_off: {
        bg: "#e0e7ff",
        text: "#3730a3",
        darkBg: "rgba(99,102,241,0.25)",
        darkText: "#a5b4fc",
      },
      on_leave: {
        bg: "#f3e8ff",
        text: "#6b21a8",
        darkBg: "rgba(168,85,247,0.25)",
        darkText: "#d8b4fe",
      },
    };
    return colors[status?.toLowerCase()] || colors.present;
  };

  const StatusBadge = ({ status, record }) => {
    const rawStatus = (record?.status || status || "present").toLowerCase();
    const colors = getStatusColor(rawStatus);
    const getIcon = () => {
      switch (rawStatus) {
        case "present":
        case "holiday_present":
        case "hp":
        case "week_off_present":
        case "wop":
        case "leave_present":
        case "lp":
          return <CheckCircleIcon sx={{ fontSize: 14 }} />;
        case "absent":
          return <CancelIcon sx={{ fontSize: 14 }} />;
        case "late":
          return <PendingIcon sx={{ fontSize: 14 }} />;
        default:
          return <CheckCircleIcon sx={{ fontSize: 14 }} />;
      }
    };

    const getLabel = () => {
      if (record?.status_label && record?.short_status) {
        return `${record.status_label} [${record.short_status}]`;
      }
      if (rawStatus === "holiday_present" || rawStatus === "hp") {
        return "Holiday Present [HP]";
      }
      if (rawStatus === "week_off_present" || rawStatus === "wop") {
        return "Week Off Present [WoP]";
      }
      if (rawStatus === "leave_present" || rawStatus === "lp") {
        return "Leave Present [LP]";
      }
      if (rawStatus === "holiday") {
        return "Holiday [H]";
      }
      if (rawStatus === "week_off") {
        return "Week Off [WO]";
      }
      if (rawStatus === "on_leave") {
        return record?.leave_name ? `On Leave (${record.leave_name})` : "On Leave";
      }
      if (rawStatus === "absent") {
        return "Absent [A]";
      }
      if (rawStatus === "late") {
        return "Late [L]";
      }
      if (rawStatus === "present") {
        return "Present [P]";
      }
      return status?.charAt(0).toUpperCase() + status?.slice(1) || "Unknown";
    };

    return (
      <Chip
        icon={getIcon()}
        label={getLabel()}
        size="small"
        sx={{
          fontWeight: 600,
          backgroundColor: colors.bg,
          color: colors.text,
          ...(theme.palette.mode === "dark" && {
            backgroundColor: colors.darkBg,
            color: colors.darkText,
          }),
        }}
      />
    );
  };

  if (!hasPermission("attendance_records.view")) {
    return null;
  }

  const exportAttendanceToExcel = () => {
    if (!filteredRecords || filteredRecords.length === 0) {
      alert("No data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    const yellowHeaderStyle = {
      font: { bold: true, sz: 12, color: { rgb: "000000" } },
      fill: { fgColor: { rgb: "FFFF00" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    let row = 0;

    XLSX.utils.sheet_add_aoa(ws, [["ATTENDANCE RECORDS REPORT"]], {
      origin: `A${row + 1}`,
    });
    ws["!merges"] = [{ s: { r: row, c: 0 }, e: { r: row, c: 7 } }];
    ws[`A${row + 1}`].s = {
      font: { bold: true, sz: 14, color: { rgb: "000000" } },
      fill: { fgColor: { rgb: "FFFF00" } },
      alignment: { horizontal: "center", vertical: "center" },
    };
    row += 2;

    XLSX.utils.sheet_add_aoa(ws, [["SUMMARY"]], { origin: `A${row + 1}` });
    ws["!merges"].push({ s: { r: row, c: 0 }, e: { r: row, c: 1 } });
    ws[`A${row + 1}`].s = yellowHeaderStyle;
    row++;

    XLSX.utils.sheet_add_aoa(
      ws,
      [
        ["Metric", "Value"],
        ["Total Records", filteredRecords.length],
        [
          "Present",
          filteredRecords.filter((r) => r.status === "present").length,
        ],
        ["Late", filteredRecords.filter((r) => r.status === "late").length],
        ["Absent", filteredRecords.filter((r) => r.status === "absent").length],
        [
          "Approved",
          filteredRecords.filter((r) => r.approval_status === "approved")
            .length,
        ],
      ],
      { origin: `A${row + 1}` },
    );
    row += 7;

    const headers = [
      "Date",
      "ID",
      "Employee Name",
      "Employee ID",
      "Phone",
      "Check In",
      "Check Out",
      "Working Hours",
      "Break Minutes",
      "Overtime",
      "Late Minutes",
      "Early Exit Minutes",
      "Status",
      "Approval Status",
    ];
    XLSX.utils.sheet_add_aoa(ws, [headers], { origin: `A${row + 1}` });

    for (let i = 0; i < headers.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: i });
      if (ws[cellRef]) {
        ws[cellRef].s = yellowHeaderStyle;
      }
    }
    row++;

    filteredRecords.forEach((record) => {
      XLSX.utils.sheet_add_aoa(
        ws,
        [
          [
            formatDateHeader(record.record_date || record.created_at),
            typeof record.id === "string" && record.id.startsWith("absent")
              ? "N/A"
              : record.id || "",
            `${record.first_name || ""} ${record.last_name || ""}`.trim(),
            record.employee_id || "",
            record.phone || "",
            record.check_in
              ? formatDateTime(record.check_in)
              : "Not Checked In",
            record.check_out
              ? formatDateTime(record.check_out)
              : record.check_in
              ? "Not Checked Out"
              : "-",
            record.working_minutes
              ? formatMinutes(record.working_minutes)
              : "0 min",
            record.break_minutes
              ? formatMinutes(record.break_minutes)
              : "0 min",
            record.overtime_minutes
              ? formatMinutes(record.overtime_minutes)
              : "0 min",
            record.late_minutes || 0,
            record.early_exit_minutes || 0,
            record.status || "absent",
            record.approval_status || "-",
          ],
        ],
        { origin: `A${row + 1}` },
      );
      row++;
    });

    ws["!cols"] = headers.map((_, index) => ({ wch: 22 }));

    XLSX.utils.book_append_sheet(wb, ws, "Attendance");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(
      blob,
      `Attendance_Report_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  const exportMonthlyAttendanceToExcel = () => {
    if (!attendanceRecords || attendanceRecords.length === 0) {
      alert("No monthly attendance data to export");
      return;
    }

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Styles
    const yellowHeaderStyle = {
      font: { bold: true, sz: 12, color: { rgb: "000000" } },
      fill: { fgColor: { rgb: "FFFF00" } },
      alignment: { horizontal: "center", vertical: "center" },
      border: {
        top: { style: "thin", color: { rgb: "000000" } },
        bottom: { style: "thin", color: { rgb: "000000" } },
        left: { style: "thin", color: { rgb: "000000" } },
        right: { style: "thin", color: { rgb: "000000" } },
      },
    };

    const statusStyles = {
      // Present statuses - Green
      P: {
        fill: { fgColor: { rgb: "D1FAE5" } },
        font: { color: { rgb: "065F46" }, bold: true },
      },
      HP: {
        fill: { fgColor: { rgb: "D1FAE5" } },
        font: { color: { rgb: "065F46" }, bold: true },
      },
      WoP: {
        fill: { fgColor: { rgb: "D1FAE5" } },
        font: { color: { rgb: "065F46" }, bold: true },
      },
      WOP: {
        fill: { fgColor: { rgb: "D1FAE5" } },
        font: { color: { rgb: "065F46" }, bold: true },
      },
      LP: {
        fill: { fgColor: { rgb: "D1FAE5" } },
        font: { color: { rgb: "065F46" }, bold: true },
      },

      // Late statuses - Yellow/Orange
      L: {
        fill: { fgColor: { rgb: "FEF3C7" } },
        font: { color: { rgb: "92400E" }, bold: true },
      },

      // Absent - Red
      A: {
        fill: { fgColor: { rgb: "FEE2E2" } },
        font: { color: { rgb: "991B1B" }, bold: true },
      },

      // Holiday/Weekend - Blue / Indigo
      H: {
        fill: { fgColor: { rgb: "DBEAFE" } },
        font: { color: { rgb: "1E40AF" }, bold: true },
      },
      WO: {
        fill: { fgColor: { rgb: "E0E7FF" } },
        font: { color: { rgb: "3730A3" }, bold: true },
      },
      SuP: {
        fill: { fgColor: { rgb: "DBEAFE" } },
        font: { color: { rgb: "1E40AF" }, bold: true },
      },
      Sat: {
        fill: { fgColor: { rgb: "DBEAFE" } },
        font: { color: { rgb: "1E40AF" }, bold: true },
      },
      Sun: {
        fill: { fgColor: { rgb: "DBEAFE" } },
        font: { color: { rgb: "1E40AF" }, bold: true },
      },
    };

    // Default style for any other status (Cyan)
    const defaultStatusStyle = {
      fill: { fgColor: { rgb: "CFFAFE" } }, // Light Cyan
      font: { color: { rgb: "0891B2" }, bold: true }, // Dark Cyan
    };

    let row = 0;
    const year = pagination?.year || selectedMonth.year();
    const month = pagination?.month || selectedMonth.month() + 1;
    const monthName = pagination?.monthName || selectedMonth.format("MMMM");
    const monthStr = String(month).padStart(2, "0");

    // Get days in month
    const daysInMonth = Array.from({ length: 31 }, (_, i) => i + 1);

    // ===== TITLE ROW =====
    XLSX.utils.sheet_add_aoa(ws, [[`Report - ${monthName} ${year}`]], {
      origin: `A${row + 1}`,
    });
    ws["!merges"] = [{ s: { r: row, c: 0 }, e: { r: row, c: 32 } }];
    ws[`A${row + 1}`].s = {
      font: { bold: true, sz: 14, color: { rgb: "000000" } },
      fill: { fgColor: { rgb: "FFFF00" } },
      alignment: { horizontal: "center", vertical: "center" },
    };
    row += 2;

    // ===== HEADER ROW 1: Emp ID | Emp Name | 1 | 2 | 3 | ... | 31 =====
    const headerRow1 = ["Emp ID", "Emp Name", ...daysInMonth];
    XLSX.utils.sheet_add_aoa(ws, [headerRow1], { origin: `A${row + 1}` });

    for (let i = 0; i < headerRow1.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: i });
      if (ws[cellRef]) {
        ws[cellRef].s = yellowHeaderStyle;
      }
    }
    row++;

    // ===== HEADER ROW 2: (empty) | (empty) | Sat | Sun | Mon | ... =====
    const headerRow2 = ["", ""];
    for (let day = 1; day <= 31; day++) {
      const date = dayjs(`${year}-${monthStr}-${String(day).padStart(2, "0")}`);
      headerRow2.push(date.isValid() ? date.format("ddd") : "");
    }
    XLSX.utils.sheet_add_aoa(ws, [headerRow2], { origin: `A${row + 1}` });

    // Apply header style
    for (let i = 0; i < headerRow2.length; i++) {
      const cellRef = XLSX.utils.encode_cell({ r: row, c: i });
      if (ws[cellRef]) {
        ws[cellRef].s = {
          font: { bold: true, color: { rgb: "000000" } },
          fill: { fgColor: { rgb: "E5E7EB" } },
          alignment: { horizontal: "center", vertical: "center" },
        };
      }
    }
    row++;

    // ===== PROCESS EACH EMPLOYEE =====
    attendanceRecords.forEach((employee) => {
      const attendance = employee.attendance || {};
      const empId = employee.employeeId || "";
      const empName = employee.employeeName || "";
      const empCode = employee.employeeCode || "";

      // ===== EMPLOYEE NAME ROW =====
      XLSX.utils.sheet_add_aoa(
        ws,
        [
          [
            `Emp Id: ${empId}    Code: ${empCode}`,
            empName,
            ...Array(30).fill(""),
          ],
        ],
        { origin: `A${row + 1}` },
      );
      ws["!merges"] = [{ s: { r: row, c: 0 }, e: { r: row, c: 0 } }];
      ws[`A${row + 1}`].s = {
        font: { bold: true, sz: 11 },
        fill: { fgColor: { rgb: "FEF3C7" } },
        alignment: { horizontal: "left", vertical: "center" },
      };
      ws[`B${row + 1}`].s = {
        font: { bold: true, sz: 11 },
        fill: { fgColor: { rgb: "FEF3C7" } },
        alignment: { horizontal: "center", vertical: "center" },
      };
      row++;

      // ===== STATUS ROW =====
      const statusRow = ["Status", ""];
      for (let day = 1; day <= 31; day++) {
        const record = attendance[day] || { status: "A" };
        statusRow.push(record.status || "A");
      }
      XLSX.utils.sheet_add_aoa(ws, [statusRow], { origin: `A${row + 1}` });
      row++;

      // ===== INTIME ROW =====
      const intimeRow = ["Intime", ""];
      for (let day = 1; day <= 31; day++) {
        const record = attendance[day] || { intime: null };
        intimeRow.push(record.intime || "");
      }
      XLSX.utils.sheet_add_aoa(ws, [intimeRow], { origin: `A${row + 1}` });
      row++;

      // ===== OUTTIME ROW =====
      const outtimeRow = ["OutTime", ""];
      for (let day = 1; day <= 31; day++) {
        const record = attendance[day] || { outtime: null };
        outtimeRow.push(record.outtime || "");
      }
      XLSX.utils.sheet_add_aoa(ws, [outtimeRow], { origin: `A${row + 1}` });
      row++;

      // ===== TOTAL HOURS ROW =====
      const totalRow = ["Total Hours", ""];
      for (let day = 1; day <= 31; day++) {
        const record = attendance[day] || { intime: null, outtime: null };
        let total = "";
        if (record.intime && record.outtime) {
          try {
            const parseTime = (timeStr) => {
              const parts = timeStr.trim().split(" ");
              if (parts.length === 2) {
                const [time, modifier] = parts;
                let [hours, minutes] = time.split(":").map(Number);
                if (modifier === "PM" && hours !== 12) hours += 12;
                if (modifier === "AM" && hours === 12) hours = 0;
                return hours * 60 + minutes;
              }
              return 0;
            };
            const inMins = parseTime(record.intime);
            const outMins = parseTime(record.outtime);
            if (inMins > 0 && outMins > 0) {
              let diff = outMins - inMins;
              if (diff < 0) diff += 1440;
              const hrs = Math.floor(diff / 60);
              const mins = diff % 60;
              total = `${String(hrs).padStart(2, "0")}:${String(mins).padStart(
                2,
                "0",
              )}`;
            }
          } catch (e) {
            total = "";
          }
        }
        totalRow.push(total);
      }
      XLSX.utils.sheet_add_aoa(ws, [totalRow], { origin: `A${row + 1}` });
      row++;

      // ===== APPLY STATUS COLORS TO STATUS ROW =====
      const statusRowIndex = row - 4; // 4 rows back (Status row)
      for (let day = 1; day <= 31; day++) {
        const record = attendance[day] || { status: "A" };
        const status = record.status || "A";

        // Map status to style key
        let styleKey = "A";
        if (
          status === "P" ||
          status === "SP" ||
          status === "HP" ||
          status === "WoP" ||
          status === "WOP"
        )
          styleKey = "P";
        else if (status === "LP" || status === "L" || status === "HPL")
          styleKey = "L";
        else if (
          status === "H" ||
          status === "WO" ||
          status === "SuP" ||
          status === "Sat" ||
          status === "Sun"
        )
          styleKey = "H";

        const cellRef = XLSX.utils.encode_cell({
          r: statusRowIndex,
          c: day + 1,
        });
        if (ws[cellRef]) {
          ws[cellRef].s = statusStyles[status] || defaultStatusStyle;
          ws[cellRef].s.alignment = {
            horizontal: "center",
            vertical: "center",
          };
        }
      }

      // ===== ADD EMPTY ROW BETWEEN EMPLOYEES =====
      row++;
    });

    // ===== SET COLUMN WIDTHS =====
    const colWidths = [
      { wch: 25 }, // Emp ID and code
      { wch: 32 }, // Emp Name
    ];
    for (let i = 0; i < 31; i++) {
      colWidths.push({ wch: 10 });
    }
    ws["!cols"] = colWidths;

    XLSX.utils.book_append_sheet(wb, ws, "Employee Attendance");

    const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([wbout], { type: "application/octet-stream" });
    saveAs(
      blob,
      `Employee_Attendance_${monthName}_${year}_${
        new Date().toISOString().split("T")[0]
      }.xlsx`,
    );
  };

  const exportMonthlyAttendanceToPDF = () => {
    if (!attendanceRecords || attendanceRecords.length === 0) {
      alert("No monthly attendance data to export");
      return;
    }

    const year = pagination?.year || selectedMonth.year();
    const month = pagination?.month || selectedMonth.month() + 1;
    const monthName = pagination?.monthName || selectedMonth.format("MMMM");
    const monthStr = String(month).padStart(2, "0");

    // Get days in month
    const daysInMonth = Array.from({ length: 31 }, (_, i) => i + 1);

    // Create PDF
    const doc = new jsPDF("landscape", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();

    // Title
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.text(`Attendance Report - ${monthName} ${year}`, pageWidth / 2, 15, {
      align: "center",
    });

    // Process each employee
    let yPosition = 25;

    attendanceRecords.forEach((employee, empIndex) => {
      const attendance = employee.attendance || {};
      const empId = employee.employeeId || "";
      const empName = employee.employeeName || "";
      const empCode = employee.employeeCode || "";

      // Check if we need a new page
      if (yPosition > 250) {
        doc.addPage();
        yPosition = 20;
      }

      // Employee header
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(0, 0, 0);
      doc.text(`Employee: ${empName} (${empCode})`, 14, yPosition);
      yPosition += 6;

      // Prepare table data
      const statusRow = ["Status"];
      const intimeRow = ["In Time"];
      const outtimeRow = ["Out Time"];
      const totalRow = ["Total Hours"];

      for (let day = 1; day <= 31; day++) {
        const record = attendance[day] || {
          status: "A",
          intime: null,
          outtime: null,
        };
        statusRow.push(record.status || "A");
        intimeRow.push(record.intime || "-");
        outtimeRow.push(record.outtime || "-");

        // Calculate total hours
        let total = "-";
        if (record.intime && record.outtime) {
          try {
            const parseTime = (timeStr) => {
              const parts = timeStr.trim().split(" ");
              if (parts.length === 2) {
                const [time, modifier] = parts;
                let [hours, minutes] = time.split(":").map(Number);
                if (modifier === "PM" && hours !== 12) hours += 12;
                if (modifier === "AM" && hours === 12) hours = 0;
                return hours * 60 + minutes;
              }
              return 0;
            };
            const inMins = parseTime(record.intime);
            const outMins = parseTime(record.outtime);
            if (inMins > 0 && outMins > 0) {
              let diff = outMins - inMins;
              if (diff < 0) diff += 1440;
              const hrs = Math.floor(diff / 60);
              const mins = diff % 60;
              total = `${hrs}h ${mins}m`;
            }
          } catch (e) {
            total = "-";
          }
        }
        totalRow.push(total);
      }

      // Add day numbers as header
      const dayHeaders = ["Day", ...daysInMonth.map(String)];

      // Create table data with all rows
      const tableData = [
        dayHeaders,
        [
          "",
          ...daysInMonth.map((day) => {
            const date = dayjs(
              `${year}-${monthStr}-${String(day).padStart(2, "0")}`,
            );
            return date.isValid() ? date.format("ddd") : "";
          }),
        ],
        statusRow,
        intimeRow,
        outtimeRow,
        totalRow,
      ];

      // Generate table with autoTable
      autoTable(doc, {
        startY: yPosition,
        head: [tableData[0]],
        body: tableData.slice(1),
        theme: "grid",
        styles: {
          fontSize: 6,
          cellPadding: 1,
          valign: "middle",
          halign: "center",
          lineColor: [0, 0, 0],
          lineWidth: 0.1,
        },
        headStyles: {
          fillColor: [255, 255, 0],
          textColor: [0, 0, 0],
          fontSize: 7,
          fontStyle: "bold",
        },
        columnStyles: {
          0: { cellWidth: 15, fontStyle: "bold", fillColor: [245, 158, 11] },
        },
        didDrawCell: (data) => {
          // Color status cells based on status
          if (data.section === "body" && data.column.index > 0) {
            const rowIndex = data.row.index;
            const cellValue = data.cell.text[0];

            // Status row (index 2 in body)
            if (rowIndex === 2) {
              const statusColors = {
                P: [209, 250, 229],
                SP: [209, 250, 229],
                HP: [209, 250, 229],
                WoP: [209, 250, 229],
                WOP: [209, 250, 229],
                LP: [209, 250, 229],
                L: [254, 243, 199],
                HPL: [254, 243, 199],
                A: [254, 226, 226],
                H: [219, 234, 254],
                WO: [224, 231, 255],
                SuP: [219, 234, 254],
                Sat: [219, 234, 254],
                Sun: [219, 234, 254],
              };
              const colors = statusColors[cellValue];
              if (colors) {
                data.cell.styles.fillColor = colors;
              }
            }

            // Total Hours row (index 5 in body) - color based on status
            if (rowIndex === 5) {
              const statusCell =
                data.table.body[rowIndex - 3]?.cells?.[data.column.index];
              if (statusCell) {
                const status = statusCell.text[0];
                if (["L", "HPL"].includes(status)) {
                  data.cell.styles.fillColor = [254, 243, 199];
                  data.cell.styles.textColor = [0, 0, 0];
                } else if (status === "A") {
                  data.cell.styles.fillColor = [254, 226, 226];
                  data.cell.styles.textColor = [0, 0, 0];
                } else if (["P", "SP", "HP", "WoP", "WOP", "LP"].includes(status)) {
                  data.cell.styles.fillColor = [209, 250, 229];
                  data.cell.styles.textColor = [0, 0, 0];
                }
              }
            }
          }
        },
        margin: { left: 10, right: 10 },
      });

      // Get the final Y position after the table
      yPosition = doc.lastAutoTable.finalY + 6;

      // Add a separator line between employees
      if (empIndex < attendanceRecords.length - 1) {
        doc.setDrawColor(200, 200, 200);
        doc.line(14, yPosition - 3, pageWidth - 14, yPosition - 3);
        yPosition += 3;
      }
    });

    // Save the PDF
    doc.save(`Attendance_Report_${monthName}_${year}.pdf`);
  };

  // const exportEmployeeReportToExcel = async () => {
  //   try {
  //     // Show loading state
  //     setExportLoading(true);

  //     const year = selectedMonth.year();
  //     const month = selectedMonth.month() + 1;
  //     const monthName = selectedMonth.format("MMMM");

  //     // Fetch the report data
  //     const result = await dispatch(
  //       fetchEmployeeLeaveReport({
  //         year,
  //         month,
  //         limit: 1000,
  //       }),
  //     );

  //     if (!result.success || !result.data || result.data.length === 0) {
  //       alert("No employee report data to export");
  //       setExportLoading(false);
  //       return;
  //     }

  //     const reportData = result.data;

  //     const wb = XLSX.utils.book_new();
  //     const ws = XLSX.utils.aoa_to_sheet([]);

  //     // Styles
  //     const headerStyle = {
  //       font: { bold: true, sz: 12, color: { rgb: "000000" } },
  //       fill: { fgColor: { rgb: "FFFF00" } },
  //       alignment: { horizontal: "center", vertical: "center" },
  //       border: {
  //         top: { style: "thin", color: { rgb: "000000" } },
  //         bottom: { style: "thin", color: { rgb: "000000" } },
  //         left: { style: "thin", color: { rgb: "000000" } },
  //         right: { style: "thin", color: { rgb: "000000" } },
  //       },
  //     };

  //     const subHeaderStyle = {
  //       font: { bold: true, sz: 10, color: { rgb: "000000" } },
  //       fill: { fgColor: { rgb: "E5E7EB" } },
  //       alignment: { horizontal: "center", vertical: "center" },
  //       border: {
  //         top: { style: "thin", color: { rgb: "000000" } },
  //         bottom: { style: "thin", color: { rgb: "000000" } },
  //         left: { style: "thin", color: { rgb: "000000" } },
  //         right: { style: "thin", color: { rgb: "000000" } },
  //       },
  //     };

  //     let row = 0;

  //     // ===== TITLE ROW =====
  //     XLSX.utils.sheet_add_aoa(
  //       ws,
  //       [[`Employee Leave Report - ${monthName} ${year}`]],
  //       {
  //         origin: `A${row + 1}`,
  //       },
  //     );
  //     ws["!merges"] = [{ s: { r: row, c: 0 }, e: { r: row, c: 10 } }];
  //     ws[`A${row + 1}`].s = {
  //       font: { bold: true, sz: 14, color: { rgb: "000000" } },
  //       fill: { fgColor: { rgb: "FFFF00" } },
  //       alignment: { horizontal: "center", vertical: "center" },
  //     };
  //     row += 2;

  //     // ===== HEADER ROW =====
  //     const headers = [
  //       "Employee ID",
  //       "Employee Code",
  //       "Employee Name",
  //       "Phone",
  //       "Email",
  //       "Designation",
  //       "Department",
  //       "Working Days",
  //       "Present",
  //       "Absent",
  //       "Holiday",
  //       "Week Off",
  //       "Total Leave",
  //       "Late",
  //       "Leave Breakdown",
  //     ];
  //     XLSX.utils.sheet_add_aoa(ws, [headers], { origin: `A${row + 1}` });

  //     for (let i = 0; i < headers.length; i++) {
  //       const cellRef = XLSX.utils.encode_cell({ r: row, c: i });
  //       if (ws[cellRef]) {
  //         ws[cellRef].s = headerStyle;
  //       }
  //     }
  //     row++;

  //     // ===== PROCESS EACH EMPLOYEE =====
  //     reportData.forEach((employee) => {
  //       const summary = employee.summary || {};
  //       const leaveBreakdown = employee.leaveBreakdown || {};

  //       // Format leave breakdown as string
  //       let leaveBreakdownStr = "";
  //       Object.entries(leaveBreakdown).forEach(([code, data]) => {
  //         if (data && data.leaveName) {
  //           leaveBreakdownStr += `${code.toUpperCase()}: ${
  //             data.leaveName
  //           } (ELG: ${data.elg || 0}, AVL: ${data.avl || 0}, RMN: ${
  //             data.rmn || 0
  //           })\n`;
  //         }
  //       });

  //       const rowData = [
  //         employee.employeeId || "",
  //         employee.employeeCode || "",
  //         employee.employeeName || "",
  //         employee.phone || "",
  //         employee.email || "",
  //         employee.designation || "",
  //         employee.department || "",
  //         summary.workingDays || 0,
  //         summary.present || 0,
  //         summary.absent || 0,
  //         summary.holiday || 0,
  //         summary.weekOff || 0,
  //         summary.totalLeave || 0,
  //         summary.late || 0,
  //         leaveBreakdownStr.trim(),
  //       ];

  //       XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: `A${row + 1}` });
  //       row++;
  //     });

  //     // ===== SET COLUMN WIDTHS =====
  //     ws["!cols"] = [
  //       { wch: 15 }, // Employee ID
  //       { wch: 15 }, // Employee Code
  //       { wch: 25 }, // Employee Name
  //       { wch: 15 }, // Phone
  //       { wch: 30 }, // Email
  //       { wch: 20 }, // Designation
  //       { wch: 18 }, // Department
  //       { wch: 14 }, // Working Days
  //       { wch: 12 }, // Present
  //       { wch: 12 }, // Absent
  //       { wch: 12 }, // Holiday
  //       { wch: 12 }, // Week Off
  //       { wch: 14 }, // Total Leave
  //       { wch: 12 }, // Late
  //       { wch: 50 }, // Leave Breakdown
  //     ];

  //     XLSX.utils.book_append_sheet(wb, ws, "Employee Leave Report");

  //     const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  //     const blob = new Blob([wbout], { type: "application/octet-stream" });
  //     saveAs(
  //       blob,
  //       `Employee_Leave_Report_${monthName}_${year}_${
  //         new Date().toISOString().split("T")[0]
  //       }.xlsx`,
  //     );

  //     setExportLoading(false);
  //   } catch (error) {
  //     console.error("Export employee report error:", error);
  //     alert("Failed to export employee report");
  //     setExportLoading(false);
  //   }
  // };

  const exportEmployeeReportToExcel = async () => {
    try {
      setExportLoading(true);

      const year = selectedMonth.year();
      const month = selectedMonth.month() + 1;
      const monthName = selectedMonth.format("MMMM");

      const result = await dispatch(
        fetchEmployeeLeaveReport({ year, month, limit: 1000 }),
      );

      if (!result.success || !result.data || result.data.length === 0) {
        alert("No employee report data to export");
        setExportLoading(false);
        return;
      }

      const reportData = result.data;
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([]);

      // 1. DYNAMICALLY EXTRACT ALL LEAVE TYPES
      // This ensures no matter what leaves are configured, they get columns.
      const leaveMap = new Map();
      reportData.forEach((emp) => {
        Object.values(emp.leaveBreakdown || {}).forEach((leave) => {
          if (leave && leave.leaveCode) {
            leaveMap.set(leave.leaveCode, leave.leaveName);
          }
        });
      });
      const leaveCodes = Array.from(leaveMap.keys());

      // Styles
      const headerStyle = {
        font: { bold: true, sz: 11, color: { rgb: "000000" } },
        fill: { fgColor: { rgb: "FFFF00" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };

      const subHeaderStyle = {
        font: { bold: true, sz: 10, color: { rgb: "000000" } },
        fill: { fgColor: { rgb: "E5E7EB" } }, // Light Gray for sub-headers
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };

      const baseHeaders = [
        "Employee ID",
        "Employee Code",
        "Employee Name",
        "Phone",
        "Email",
        "Designation",
        "Department",
        "Working Days",
        "Present",
        "Absent",
        "Holiday",
        "Week Off",
        "Total Leave",
        "Late",
      ];

      let row = 0;
      const totalCols = baseHeaders.length + leaveCodes.length * 3;

      // ===== TITLE ROW =====
      XLSX.utils.sheet_add_aoa(
        ws,
        [[`Employee Leave Report - ${monthName} ${year}`]],
        { origin: `A${row + 1}` },
      );
      ws["!merges"] = [
        { s: { r: row, c: 0 }, e: { r: row, c: totalCols - 1 } },
      ];
      ws[`A${row + 1}`].s = {
        font: { bold: true, sz: 14, color: { rgb: "000000" } },
        fill: { fgColor: { rgb: "FFFF00" } },
        alignment: { horizontal: "center", vertical: "center" },
      };
      row += 2;

      // ===== HEADER ROWS (2-Tier Structure) =====
      const leaveColors = [
        { header: "FFB6C1", body: "FFF0F5" }, // Pink
        { header: "00FFFF", body: "E0FFFF" }, // Cyan
        { header: "FFA500", body: "FFEFD5" }, // Orange
        { header: "50C878", body: "F0FFF0" }, // Emerald
        { header: "DDA0DD", body: "F8F8FF" }, // Purple
        { header: "F0E68C", body: "FFFFE0" }, // Yellow
      ];
      const headerRow1 = [...baseHeaders];
      const headerRow2 = Array(baseHeaders.length).fill(""); // Blank under base headers
      const baseColColors = {
        8: { header: "90EE90", body: "F0FFF0" }, // Green
        9: { header: "FF7F7F", body: "FFF0F0" }, // Red
        12: { header: "87CEFA", body: "F0F8FF" }, // Blue
      };
      leaveCodes.forEach((code) => {
        headerRow1.push(`${leaveMap.get(code)} (${code})`, "", ""); // Leave name taking 3 columns
        headerRow2.push("Eligible", "Availed", "Remaining"); // Sub headers
      });

      XLSX.utils.sheet_add_aoa(ws, [headerRow1, headerRow2], {
        origin: `A${row + 1}`,
      });

      // Apply header merges and styles
      for (let i = 0; i < baseHeaders.length; i++) {
        // Override specific base headers (8: Present, 9: Absent, 12: Total Leave)

        [8, 9, 12].forEach((colIdx) => {
          const customStyle = {
            ...headerStyle,
            fill: { fgColor: { rgb: baseColColors[colIdx].header } },
          };

          const topCell = XLSX.utils.encode_cell({ r: row, c: colIdx });
          const subCell = XLSX.utils.encode_cell({ r: row + 1, c: colIdx });
          if (ws[topCell]) ws[topCell].s = customStyle;
          if (ws[subCell]) ws[subCell].s = customStyle;
        });
        // Merge base headers vertically (Row 1 to Row 2)
        ws["!merges"].push({ s: { r: row, c: i }, e: { r: row + 1, c: i } });
        const cellRef1 = XLSX.utils.encode_cell({ r: row, c: i });
        const cellRef2 = XLSX.utils.encode_cell({ r: row + 1, c: i });
        if (ws[cellRef1]) ws[cellRef1].s = headerStyle;
        if (ws[cellRef2]) ws[cellRef2].s = headerStyle; // Keeps border consistent
      }

      leaveCodes.forEach((code, index) => {
        const startCol = baseHeaders.length + index * 3;
        ws["!merges"].push({
          s: { r: row, c: startCol },
          e: { r: row, c: startCol + 2 },
        });

        const theme = leaveColors[index % leaveColors.length];
        const customHeaderStyle = {
          ...headerStyle,
          fill: { fgColor: { rgb: theme.header } },
        };
        const customSubStyle = {
          ...subHeaderStyle,
          fill: { fgColor: { rgb: theme.header } },
        };

        for (let c = 0; c < 3; c++) {
          const topCell = XLSX.utils.encode_cell({ r: row, c: startCol + c });
          const subCell = XLSX.utils.encode_cell({
            r: row + 1,
            c: startCol + c,
          });
          if (ws[topCell]) ws[topCell].s = customHeaderStyle;
          if (ws[subCell]) ws[subCell].s = customSubStyle;
        }
      });

      row += 2;

      // ===== PROCESS EACH EMPLOYEE =====
      reportData.forEach((employee) => {
        const summary = employee.summary || {};
        const breakdown = employee.leaveBreakdown || {};

        const rowData = [
          employee.employeeId || "",
          employee.employeeCode || "",
          employee.employeeName || "",
          employee.phone || "",
          employee.email || "",
          employee.designation || "",
          employee.department || "",
          summary.workingDays || 0,
          summary.present || 0,
          summary.absent || 0,
          summary.holiday || 0,
          summary.weekOff || 0,
          summary.totalLeave || 0,
          summary.late || 0,
        ];

        leaveCodes.forEach((code) => {
          const leaveKey = Object.keys(breakdown).find(
            (k) => breakdown[k].leaveCode === code,
          );
          const leaveData = leaveKey ? breakdown[leaveKey] : null;
          rowData.push(
            leaveData ? leaveData.elg || 0 : 0,
            leaveData ? leaveData.avl || 0 : 0,
            leaveData ? leaveData.rmn || 0 : 0,
          );
        });

        XLSX.utils.sheet_add_aoa(ws, [rowData], { origin: `A${row + 1}` });

        // Apply body colors to the leave columns for this specific row
        leaveCodes.forEach((code, index) => {
          const startCol = baseHeaders.length + index * 3;
          const theme = leaveColors[index % leaveColors.length];
          const bodyStyle = {
            fill: { fgColor: { rgb: theme.body } },
            alignment: { horizontal: "center", vertical: "center" },
            border: headerStyle.border,
          };

          for (let c = 0; c < 3; c++) {
            const cellRef = XLSX.utils.encode_cell({ r: row, c: startCol + c });
            if (ws[cellRef]) ws[cellRef].s = bodyStyle;
          }
        });
        // Apply body colors for Present, Absent, and Total Leave
        [8, 9, 12].forEach((colIdx) => {
          const cellRef = XLSX.utils.encode_cell({ r: row, c: colIdx });
          if (ws[cellRef]) {
            ws[cellRef].s = {
              alignment: { horizontal: "center", vertical: "center" },
              border: headerStyle.border,
              fill: { fgColor: { rgb: baseColColors[colIdx].body } },
            };
          }
        });
        [7, 10, 11, 13].forEach((colIdx) => {
          const cellRef = XLSX.utils.encode_cell({ r: row, c: colIdx });
          if (ws[cellRef]) {
            ws[cellRef].s = {
              ...ws[cellRef].s,
              alignment: { horizontal: "center", vertical: "center" },
            };
          }
        });
        row++;
      });
      // ===== SET COLUMN WIDTHS =====
      const colWidths = [
        { wch: 12 },
        { wch: 15 },
        { wch: 25 },
        { wch: 15 },
        { wch: 25 }, // ID to Email
        { wch: 18 },
        { wch: 18 },
        { wch: 12 },
        { wch: 10 },
        { wch: 10 }, // Desig to Absent
        { wch: 10 },
        { wch: 10 },
        { wch: 12 },
        { wch: 10 }, // Holiday to Late
      ];

      // Add 10-character widths for every ELG/AVL/RMN column
      leaveCodes.forEach(() => {
        colWidths.push({ wch: 10 }, { wch: 10 }, { wch: 10 });
      });

      ws["!cols"] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, "Employee Leave Report");

      const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const blob = new Blob([wbout], { type: "application/octet-stream" });
      saveAs(
        blob,
        `Employee_Leave_Report_${monthName}_${year}_${
          new Date().toISOString().split("T")[0]
        }.xlsx`,
      );

      setExportLoading(false);
    } catch (error) {
      console.error("Export employee report error:", error);
      alert("Failed to export employee report");
      setExportLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        // background:
        //   theme.palette.mode === "dark"
        //     ? "linear-gradient(135deg, #0a0f1e 0%, #0f172a 50%, #1e1b2e 100%)"
        //     : "linear-gradient(135deg, #fffbeb 0%, #ffffff 50%, #fefce8 100%)",
        p: 3,
      }}
    >
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "flex-start", sm: "center" }}
          justifyContent="space-between"
          sx={{ width: "100%" }}
        >
          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
            sx={{ flex: 1 }}
          >
            <Box
              component="img"
              src={attendanceSvg}
              alt="Attendance"
              sx={{
                width: { xs: 64, lg: 80 },
                height: { xs: 64, lg: 80 },
                objectFit: "contain",
              }}
            />
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: "text.primary",
                }}
              >
                Attendance Records
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: "text.secondary",
                }}
              >
                View and monitor employee attendance activities
              </Typography>
            </Box>
          </Stack>

          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={
                viewMode === "monthly"
                  ? exportMonthlyAttendanceToExcel
                  : exportAttendanceToExcel
              }
              sx={{
                background:
                  viewMode === "monthly"
                    ? "linear-gradient(135deg, #ec4899 0%, #db2777 100%)"
                    : "linear-gradient(135deg, #25a55f 0%, #1e8b4e 100%)",
                color: "#ffffff",
                borderRadius: "8px",
                px: 2.5,
                py: 0.5,
                fontSize: "0.8125rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                height: "36px",
                "&:hover": {
                  background:
                    viewMode === "monthly"
                      ? "linear-gradient(135deg, #db2777 0%, #be185d 100%)"
                      : "linear-gradient(135deg, #1e8b4e 0%, #16723e 100%)",
                  boxShadow:
                    viewMode === "monthly"
                      ? "0 2px 8px rgba(236, 72, 153, 0.3)"
                      : "0 2px 8px rgba(37, 165, 95, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              {viewMode === "monthly"
                ? "Export Monthly View"
                : "Export Daily View"}
            </Button>
            {/* Export Monthly PDF Button */}
            {viewMode === "monthly" && (
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={exportMonthlyAttendanceToPDF}
                sx={{
                  background:
                    "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  color: "#ffffff",
                  borderRadius: "8px",
                  px: 2.5,
                  py: 0.5,
                  fontSize: "0.8125rem",
                  fontWeight: 500,
                  textTransform: "none",
                  minWidth: "auto",
                  whiteSpace: "nowrap",
                  boxShadow: "none",
                  transition: "all 0.2s ease",
                  height: "36px",
                  "&:hover": {
                    background:
                      "linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)",
                    boxShadow: "0 2px 8px rgba(239, 68, 68, 0.3)",
                    transform: "translateY(-1px)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                }}
              >
                Export Monthly View PDF
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={exportEmployeeReportToExcel}
              sx={{
                background: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                color: "#ffffff",
                borderRadius: "8px",
                px: 2.5,
                py: 0.5,
                fontSize: "0.8125rem",
                fontWeight: 500,
                textTransform: "none",
                minWidth: "auto",
                whiteSpace: "nowrap",
                boxShadow: "none",
                transition: "all 0.2s ease",
                height: "36px",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)",
                  boxShadow: "0 2px 8px rgba(139, 92, 246, 0.3)",
                  transform: "translateY(-1px)",
                },
                "&:active": {
                  transform: "translateY(0)",
                },
              }}
            >
              Export Employee Report
            </Button>
          </Box>
        </Stack>
      </Box>
      {/* View Mode Toggle */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => {
            if (newMode) {
              setViewMode(newMode);
              setPage(0);
            }
          }}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              borderRadius: "8px !important",
              px: 2.5,
              py: 0.75,
              fontSize: "0.8125rem",
              fontWeight: 500,
              minWidth: "120px",
              borderColor:
                theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
              border: `1px solid ${
                theme.palette.mode === "dark" ? "#334155" : "#e5e7eb"
              }`,
              transition: "all 0.2s ease",
              "&.Mui-selected": {
                backgroundColor: "#f59e0b !important",
                color: "#ffffff !important",
                borderColor: "#f59e0b !important",
                "&:hover": {
                  backgroundColor: "#d97706 !important",
                },
              },
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(245,158,11,0.1)"
                    : "#fffbeb",
              },
            },
            "& .MuiToggleButtonGroup-grouped": {
              margin: "0 4px", // Add spacing between buttons
              "&:not(:first-of-type)": {
                marginLeft: "4px",
              },
              "&:first-of-type": {
                marginRight: "4px",
              },
            },
          }}
        >
          <ToggleButton value="daily" sx={{ borderRadius: "8px" }}>
            Daily View
          </ToggleButton>
          <ToggleButton value="monthly" sx={{ borderRadius: "8px" }}>
            Monthly View
          </ToggleButton>
        </ToggleButtonGroup>

        {viewMode === "monthly" && (
          <>
            <DatePicker
              label="Select Month"
              views={["month", "year"]}
              value={selectedMonth}
              onChange={(newValue) => {
                if (newValue) {
                  setSelectedMonth(newValue);
                  setPage(0);
                }
              }}
              slotProps={{
                textField: {
                  size: "small",
                  sx: {
                    width: 180,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      bgcolor: "background.paper",
                    },
                  },
                },
              }}
            />
            <Button
              variant="outlined"
              startIcon={<InfoIcon />}
              onClick={handleViewLegend}
              sx={{
                borderRadius: "8px",
                px: 2,
                py: 0.75,
                fontSize: "0.8125rem",
                fontWeight: 500,
                textTransform: "none",
                borderColor: "#f59e0b",
                color: "#f59e0b",
                "&:hover": {
                  borderColor: "#d97706",
                  backgroundColor: "rgba(245, 158, 11, 0.1)",
                },
              }}
            >
              View Legend
            </Button>
          </>
        )}
      </Stack>
      {/* Search and Status Filters - Row 1 */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems="center"
        sx={{ mb: 2 }}
      >
        <Autocomplete
          size="small"
          options={employees}
          getOptionLabel={(option) =>
            typeof option === "string"
              ? option
              : `${option.first_name || ""} ${option.last_name || ""}`.trim()
          }
          value={selectedEmployee}
          onChange={(event, newValue) => {
            setSelectedEmployee(newValue);
            setPage(0);
          }}
          inputValue={searchTerm}
          onInputChange={(event, newInputValue) => {
            setSearchTerm(newInputValue);
            setPage(0);
          }}
          clearOnBlur={false}
          freeSolo
          filterOptions={(options, state) => {
            const input = (state.inputValue || "").toLowerCase();
            return options.filter((emp) => {
              const name = `${emp.first_name || ""} ${
                emp.last_name || ""
              }`.toLowerCase();
              const code = (emp.employee_code || "").toLowerCase();
              const phone = (emp.phone || "").toLowerCase();
              const email = (emp.email || "").toLowerCase();
              return (
                name.includes(input) ||
                code.includes(input) ||
                phone.includes(input) ||
                email.includes(input)
              );
            });
          }}
          renderOption={(props, option) => (
            <Box component="li" {...props} key={option.id}>
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "50%",
                    backgroundColor: "#f59e0b",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                  }}
                >
                  {option.first_name
                    ? option.first_name.charAt(0).toUpperCase()
                    : "E"}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {option.first_name} {option.last_name}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    Code: {option.employee_code || option.id} | Phone:{" "}
                    {option.phone || "-"}
                  </Typography>
                </Box>
              </Stack>
            </Box>
          )}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Search or select employee...."
              size="small"
              InputProps={{
                ...(params.InputProps || {}),
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#9ca3af" }} />
                    </InputAdornment>
                    {params.InputProps?.startAdornment}
                  </>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  bgcolor: "background.paper",
                  "& fieldset": {
                    borderColor:
                      theme.palette.mode === "dark" ? "#334155" : "#e5e7eb",
                  },
                  "&:hover fieldset": { borderColor: "#f59e0b" },
                  "&.Mui-focused fieldset": { borderColor: "#f59e0b" },
                },
              }}
            />
          )}
          sx={{ flex: 1, maxWidth: { sm: 400 } }}
        />
        {viewMode !== "monthly" && (
          <Stack direction="row" spacing={1}>
            {["all", "present", "late", "absent"].map((filter) => (
              <Button
                key={filter}
                variant={statusFilter === filter ? "contained" : "outlined"}
                onClick={() => {
                  setStatusFilter(filter);
                  setPage(0);
                }}
                size="small"
                sx={{
                  textTransform: "capitalize",
                  borderRadius: "20px",
                  px: 2,
                  minWidth: 70,
                  ...(statusFilter === filter && {
                    background:
                      "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
                    "&:hover": {
                      background:
                        "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
                    },
                  }),
                }}
              >
                {filter === "all" ? "All" : filter}
              </Button>
            ))}
          </Stack>
        )}
      </Stack>

      {/* Date Filters */}
      {viewMode !== "monthly" && (
        <Paper
          elevation={0}
          sx={{
            p: 2,
            mb: 3,
            borderRadius: "12px",
            backgroundColor: "transparent",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={3}>
              <DatePicker
                label="From Date"
                value={fromDate ? dayjs(fromDate) : null}
                onChange={(newValue) => {
                  setFromDate(
                    newValue && dayjs(newValue).isValid()
                      ? dayjs(newValue).format("YYYY-MM-DD")
                      : "",
                  );
                  setPage(0);
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    InputLabelProps: { shrink: true },
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                      },
                      "& .MuiInputLabel-root": {
                        transform: "translate(14px, -9px) scale(0.75)",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                        padding: "0 4px",
                        borderRadius: "4px",
                      },
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <DatePicker
                label="To Date"
                value={toDate ? dayjs(toDate) : null}
                onChange={(newValue) => {
                  setToDate(
                    newValue && dayjs(newValue).isValid()
                      ? dayjs(newValue).format("YYYY-MM-DD")
                      : "",
                  );
                  setPage(0);
                }}
                minDate={fromDate ? dayjs(fromDate) : undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    InputLabelProps: { shrink: true },
                    sx: {
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "10px",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                      },
                      "& .MuiInputLabel-root": {
                        transform: "translate(14px, -9px) scale(0.75)",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
                        padding: "0 4px",
                        borderRadius: "4px",
                      },
                    },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                sx={{
                  borderRadius: "10px",
                  height: 40,
                  borderColor: "#f59e0b",
                  color: "primary.main",
                  "&:hover": {
                    borderColor: "#d97706",
                    backgroundColor: "rgba(245,158,11,0.1)",
                  },
                }}
              >
                Clear Filters
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Loading */}
      {loading ? (
        <Box
          sx={{
            height: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Stack alignItems="center" spacing={2}>
            <CircularProgress sx={{ color: "primary.main" }} />
            <Typography
              sx={{
                color: "text.secondary",
              }}
            >
              Loading attendance records...
            </Typography>
          </Stack>
        </Box>
      ) : (
        <>
          <Paper
            elevation={0}
            sx={{
              borderRadius: "16px",
              overflow: "hidden",
              border: `1px solid ${
                theme.palette.mode === "dark" ? "#1e293b" : "#f3f4f6"
              }`,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 10px 25px -5px rgba(0,0,0,0.3)"
                  : "0 10px 25px -5px rgba(0,0,0,0.05)",
            }}
          >
            <TableContainer sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 1000 }}>
                <TableHead
                  sx={{
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#1e293b" : "#f9fafb",
                  }}
                >
                  {viewMode === "monthly" ? (
                    <TableRow>
                      <TableCell
                        colSpan={canApprove ? 9 : 8}
                        sx={{ py: 2, px: 3 }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: "1.1rem",
                            color: "#f59e0b",
                          }}
                        >
                          📊{" "}
                          {pagination?.monthName ||
                            selectedMonth.format("MMMM")}{" "}
                          {pagination?.year || selectedMonth.year()} - Monthly
                          Attendance Report
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        Employee Details
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        Check In
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        Check Out
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        Status Info
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        Working Details
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        Overtime
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem" }}>
                        Approval
                      </TableCell>
                      {canApprove && (
                        <TableCell
                          sx={{ fontWeight: 700, fontSize: "0.75rem" }}
                        >
                          Actions
                        </TableCell>
                      )}
                    </TableRow>
                  )}
                </TableHead>

                <TableBody>
                  {viewMode === "monthly" ? (
                    attendanceRecords.length > 0 ? (
                      <TableRow>
                        <TableCell colSpan={canApprove ? 9 : 8} sx={{ p: 0 }}>
                          <Box sx={{ p: 3 }}>
                            <Stack spacing={4}>
                              {memoizedEmployees
                                .filter((emp) => {
                                  if (!searchTerm) return true;
                                  const search = searchTerm.toLowerCase();
                                  const name = (
                                    emp.employeeName || ""
                                  ).toLowerCase();
                                  const code = (
                                    emp.employeeCode || ""
                                  ).toLowerCase();
                                  return (
                                    name.includes(search) ||
                                    code.includes(search)
                                  );
                                })
                                .map((employee) => (
                                  <AttendanceMonthlyToggle
                                    key={employee.employeeId}
                                    ref={(el) =>
                                      (employeeRefs.current[
                                        employee.employeeId
                                      ] = el)
                                    }
                                    employee={employee}
                                    year={employee.year}
                                    month={employee.month}
                                    monthStr={employee.monthStr}
                                    daysInMonth={employee.daysInMonth}
                                    theme={theme}
                                    pagination={pagination}
                                    selectedMonth={selectedMonth}
                                  />
                                ))}
                            </Stack>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      <TableRow>
                        <TableCell
                          colSpan={canApprove ? 9 : 8}
                          align="center"
                          sx={{ py: 8 }}
                        >
                          <EventAvailableIcon
                            sx={{ fontSize: 50, color: "#d1d5db", mb: 1 }}
                          />
                          <Typography>
                            No attendance data for this month
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )
                  ) : Object.keys(groupedRecordsByDate).length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={canApprove ? 9 : 8}
                        align="center"
                        sx={{ py: 8 }}
                      >
                        <EventAvailableIcon
                          sx={{ fontSize: 50, color: "#d1d5db", mb: 1 }}
                        />
                        <Typography>No attendance records found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    Object.keys(groupedRecordsByDate).map((dateKey) => (
                      <React.Fragment key={`date-group-${dateKey}`}>
                        <TableRow
                          sx={{
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "rgba(30, 41, 59, 0.9)"
                                : "#f1f5f9",
                          }}
                        >
                          <TableCell
                            colSpan={canApprove ? 9 : 8}
                            sx={{ py: 1.2, px: 2 }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1.5}
                            >
                              <CalendarTodayIcon
                                sx={{ fontSize: 18, color: "primary.main" }}
                              />
                              <Typography
                                sx={{
                                  fontWeight: 700,
                                  fontSize: "1rem",
                                  color:
                                    theme.palette.mode === "dark"
                                      ? "#f59e0b"
                                      : "#d97706",
                                  letterSpacing: "0.2px",
                                }}
                              >
                                {formatDateHeader(dateKey)}
                              </Typography>
                            </Stack>
                          </TableCell>
                        </TableRow>

                        {groupedRecordsByDate[dateKey].map((record, index) => (
                          <TableRow
                            key={
                              record.id || `${record.employee_id}-${dateKey}`
                            }
                            component={motion.tr}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.03 }}
                          >
                            <TableCell>
                              <Stack
                                direction="row"
                                spacing={1.5}
                                alignItems="center"
                              >
                                <Box>
                                  <Typography sx={{ fontWeight: 600 }}>
                                    {record.first_name} {record.last_name}
                                  </Typography>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                    flexWrap="wrap"
                                  >
                                    <Stack
                                      direction="row"
                                      spacing={0.5}
                                      alignItems="center"
                                    >
                                      <BadgeIcon
                                        sx={{ fontSize: 12, color: "#9ca3af" }}
                                      />
                                      <Typography
                                        sx={{
                                          fontSize: "0.7rem",
                                          color: "#9ca3af",
                                        }}
                                      >
                                        ID: {record.employee_id}
                                      </Typography>
                                    </Stack>
                                    <Stack
                                      direction="row"
                                      spacing={0.5}
                                      alignItems="center"
                                    >
                                      <PhoneIcon
                                        sx={{ fontSize: 12, color: "#10b981" }}
                                      />
                                      <Typography
                                        sx={{
                                          fontSize: "0.7rem",
                                          color: "#9ca3af",
                                        }}
                                      >
                                        {record.phone || "-"}
                                      </Typography>
                                    </Stack>
                                  </Stack>
                                </Box>
                              </Stack>
                            </TableCell>

                            <TableCell>
                              {record.check_in ? (
                                <Stack spacing={0.5}>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <AccessTimeIcon
                                      sx={{ fontSize: 15, color: "#10b981" }}
                                    />
                                    <Typography
                                      sx={{
                                        fontSize: "0.8rem",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {formatDateTime(record.check_in)}
                                    </Typography>
                                  </Stack>
                                  {record.is_late === 1 && (
                                    <Chip
                                      label={`${record.late_minutes} min late`}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#fee2e2",
                                        color: "#991b1b",
                                        fontWeight: 600,
                                        width: "fit-content",
                                      }}
                                    />
                                  )}
                                  {record.check_in_picture && (
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        setSelectedPicture(
                                          record.check_in_picture,
                                        );
                                        setCheckInModalOpen(true);
                                      }}
                                      sx={{
                                        textTransform: "none",
                                        color: "#3b82f6",
                                        fontSize: "0.7rem",
                                        p: 0,
                                        justifyContent: "flex-start",
                                      }}
                                    >
                                      View Picture
                                    </Button>
                                  )}
                                </Stack>
                              ) : (
                                <Typography
                                  sx={{ fontSize: "0.8rem", color: "#9ca3af" }}
                                >
                                  -
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              {record.check_out ? (
                                <Stack spacing={0.5}>
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <LogoutIcon
                                      sx={{ fontSize: 15, color: "#ef4444" }}
                                    />
                                    <Typography
                                      sx={{
                                        fontSize: "0.8rem",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {formatDateTime(record.check_out)}
                                    </Typography>
                                  </Stack>
                                  {record.is_early_exit === 1 && (
                                    <Chip
                                      label={`${record.early_exit_minutes} min early exit`}
                                      size="small"
                                      sx={{
                                        backgroundColor: "#fef3c7",
                                        color: "#92400e",
                                        fontWeight: 600,
                                        width: "fit-content",
                                      }}
                                    />
                                  )}
                                  {record.check_out_picture && (
                                    <Button
                                      size="small"
                                      onClick={() => {
                                        setSelectedPicture(
                                          record.check_out_picture,
                                        );
                                        setCheckOutModalOpen(true);
                                      }}
                                      sx={{
                                        textTransform: "none",
                                        color: "#3b82f6",
                                        fontSize: "0.7rem",
                                        p: 0,
                                        justifyContent: "flex-start",
                                      }}
                                    >
                                      View Picture
                                    </Button>
                                  )}
                                </Stack>
                              ) : record.check_in ? (
                                <Chip
                                  label="Not Checked Out"
                                  size="small"
                                  sx={{
                                    backgroundColor: "#fef3c7",
                                    color: "#92400e",
                                    fontWeight: 600,
                                  }}
                                />
                              ) : (
                                <Typography
                                  sx={{ fontSize: "0.8rem", color: "#9ca3af" }}
                                >
                                  -
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell>
                              <Stack spacing={1} alignItems="center">
                                <StatusBadge status={record.status} record={record} />
                                <Typography
                                  sx={{
                                    fontSize: "0.7rem",
                                    color: "#9ca3af",
                                    textAlign: "center",
                                  }}
                                >
                                  {formatDateOnly(
                                    record.created_at || record.record_date,
                                  )}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell>
                              <Stack spacing={0.5}>
                                <Typography
                                  sx={{
                                    fontWeight: 700,
                                    color: record.check_in
                                      ? "#10b981"
                                      : "#9ca3af",
                                  }}
                                >
                                  {formatMinutes(record.working_minutes)}
                                </Typography>
                                {record.shift_id && (
                                  <Typography
                                    sx={{
                                      fontSize: "0.7rem",
                                      color: "#9ca3af",
                                    }}
                                  >
                                    Shift: {record.shift_id}
                                  </Typography>
                                )}
                                <Typography
                                  sx={{ fontSize: "0.7rem", color: "#9ca3af" }}
                                >
                                  Break: {formatMinutes(record.break_minutes)}
                                </Typography>
                              </Stack>
                            </TableCell>

                            <TableCell>
                              <Typography
                                sx={{
                                  color:
                                    record.overtime_minutes > 0
                                      ? "#f59e0b"
                                      : "#9ca3af",
                                  fontWeight:
                                    record.overtime_minutes > 0 ? 700 : 400,
                                }}
                              >
                                {formatMinutes(record.overtime_minutes)}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              {record.approval_status ? (
                                <Chip
                                  label={
                                    record.approval_status
                                      .charAt(0)
                                      .toUpperCase() +
                                    record.approval_status.slice(1)
                                  }
                                  size="small"
                                  sx={{
                                    textTransform: "capitalize",
                                    backgroundColor:
                                      record.approval_status === "approved"
                                        ? "#d1fae5"
                                        : record.approval_status === "rejected"
                                        ? "#fee2e2"
                                        : "#fef3c7",
                                    color:
                                      record.approval_status === "approved"
                                        ? "#065f46"
                                        : record.approval_status === "rejected"
                                        ? "#991b1b"
                                        : "#92400e",
                                    fontWeight: 600,
                                  }}
                                />
                              ) : (
                                <Typography
                                  sx={{ fontSize: "0.8rem", color: "#9ca3af" }}
                                >
                                  -
                                </Typography>
                              )}
                            </TableCell>

                            {canApprove && (
                              <TableCell>
                                {record.approval_status === "pending" ? (
                                  <Stack
                                    direction="row"
                                    spacing={1}
                                    alignItems="center"
                                  >
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="success"
                                      startIcon={
                                        <CheckCircleIcon
                                          sx={{ fontSize: 15 }}
                                        />
                                      }
                                      disabled={actionLoadingId === record.id}
                                      onClick={() =>
                                        handleVerify(record.id, "approved")
                                      }
                                      sx={{
                                        textTransform: "none",
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                        borderRadius: "8px",
                                        px: 1.5,
                                        py: 0.4,
                                        backgroundColor: "#10b981",
                                        color: "#ffffff",
                                        "&:hover": {
                                          backgroundColor: "#059669",
                                        },
                                      }}
                                    >
                                      Approve
                                    </Button>
                                    <Button
                                      size="small"
                                      variant="contained"
                                      color="error"
                                      startIcon={
                                        <CancelIcon sx={{ fontSize: 15 }} />
                                      }
                                      disabled={actionLoadingId === record.id}
                                      onClick={() =>
                                        handleVerify(record.id, "rejected")
                                      }
                                      sx={{
                                        textTransform: "none",
                                        fontSize: "0.75rem",
                                        fontWeight: 600,
                                        borderRadius: "8px",
                                        px: 1.5,
                                        py: 0.4,
                                        backgroundColor: "#ef4444",
                                        color: "#ffffff",
                                        "&:hover": {
                                          backgroundColor: "#dc2626",
                                        },
                                      }}
                                    >
                                      Reject
                                    </Button>
                                  </Stack>
                                ) : (
                                  <Typography
                                    sx={{
                                      fontSize: "0.75rem",
                                      color: "#9ca3af",
                                    }}
                                  >
                                    -
                                  </Typography>
                                )}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </React.Fragment>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          {viewMode !== "monthly" && (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100]}
              component="div"
              count={pagination?.total_records || filteredRecords.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </>
      )}

      {/* Picture Modal */}
      <Dialog
        open={checkInModalOpen || checkOutModalOpen}
        onClose={() => {
          setCheckInModalOpen(false);
          setCheckOutModalOpen(false);
          setSelectedPicture("");
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "16px",
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          {checkInModalOpen ? "Check In Picture" : "Check Out Picture"}
          <IconButton
            onClick={() => {
              setCheckInModalOpen(false);
              setCheckOutModalOpen(false);
              setSelectedPicture("");
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ textAlign: "center", p: 3 }}>
          <img
            src={selectedPicture}
            alt="Attendance"
            style={{
              maxWidth: "100%",
              maxHeight: "500px",
              borderRadius: "12px",
              objectFit: "contain",
            }}
          />
        </DialogContent>
      </Dialog>
      {/* Legend Modal */}
      <Dialog
        open={legendModalOpen}
        onClose={() => setLegendModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "12px",
            backgroundColor:
              theme.palette.mode === "dark" ? "#0f172a" : "#ffffff",
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 600, fontSize: "1rem", pb: 1 }}>
          Codes with their full forms
        </DialogTitle>
        <DialogContent sx={{ pt: 0 }}>
          {loadingLegend ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={30} sx={{ color: "#f59e0b" }} />
            </Box>
          ) : (
            <Stack spacing={1.5}>
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  color: "#f59e0b",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                }}
              >
                Attendance Status Codes
              </Typography>
              {[
                { code: "P", desc: "Present" },
                { code: "L", desc: "Late Present" },
                { code: "HP", desc: "Holiday Present" },
                { code: "WoP", desc: "Week Off Present" },
                { code: "LP", desc: "Leave Present" },
                { code: "H", desc: "Holiday" },
                { code: "WO", desc: "Week Off" },
                { code: "A", desc: "Absent" },
              ].map(({ code, desc }) => (
                <Box
                  key={code}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    px: 1.5,
                    py: 0.8,
                    borderRadius: "6px",
                    backgroundColor:
                      theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
                  }}
                >
                  <Typography
                    sx={{
                      fontWeight: 700,
                      fontSize: "0.8rem",
                      color: "#10b981",
                      minWidth: 45,
                    }}
                  >
                    {code}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      color:
                        theme.palette.mode === "dark" ? "#94a3b8" : "#475569",
                    }}
                  >
                    {desc}
                  </Typography>
                </Box>
              ))}

              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  color: "#f59e0b",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  pt: 1,
                }}
              >
                Leave Type Codes
              </Typography>
              {leaveCodes &&
                Object.entries(leaveCodes).map(([code, description]) => (
                  <Box
                    key={code}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      px: 1.5,
                      py: 0.8,
                      borderRadius: "6px",
                      backgroundColor:
                        theme.palette.mode === "dark" ? "#1e293b" : "#f1f5f9",
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "0.8rem",
                        color: "#f59e0b",
                        minWidth: 45,
                      }}
                    >
                      {code.toUpperCase()}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.8rem",
                        color:
                          theme.palette.mode === "dark" ? "#94a3b8" : "#475569",
                        textTransform: "capitalize",
                      }}
                    >
                      {description}
                    </Typography>
                  </Box>
                ))}
              {Object.keys(leaveCodes || {}).length === 0 && (
                <Typography
                  sx={{
                    textAlign: "center",
                    color: "#94a3b8",
                    py: 1,
                    fontSize: "0.85rem",
                  }}
                >
                  No leave codes available
                </Typography>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button
            onClick={() => setLegendModalOpen(false)}
            size="small"
            sx={{
              textTransform: "none",
              borderRadius: "6px",
              px: 3,
              background: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
              color: "#ffffff",
              fontSize: "0.8rem",
              "&:hover": {
                background: "linear-gradient(135deg, #d97706 0%, #b45309 100%)",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AttendanceRecords;
